import { yupResolver } from '@hookform/resolvers/yup'
import { formatDate } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { ControlledDateOnlyPicker, ControlledTextField } from '@if/mui'
import { Alert, Stack } from '@mui/material'
import { usePostMultipleApprovedRemoteExceptions } from '@src/api/exception-api'
import { ApprovedRemoteExceptionsQueryParams } from '@src/types/ApprovedRemoteExceptions'
import { ApprovedRemoteExceptionsListDto } from '@src/types/Dto/ApprovedRemoteExceptionsListDto'
import { Person } from '@src/types/Person'
import { useMemo } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FormsDialog from '../Common/Dialog/FormsDialog'
import PersonListInput from './PersonListInput'

interface ApprovedRemoteExceptionsAddDialogProps {
  open: boolean
  onCloseAdd: () => void
  persons: Person[]
  queryParams: ApprovedRemoteExceptionsQueryParams
}
interface FormValues extends FieldValues {
  persons: Person[]
  startDate: string
  endDate?: string | undefined
  reason: string
}

const ApprovedRemoteExceptionsAddDialog = ({
  open,
  onCloseAdd,
  persons,
  queryParams,
}: ApprovedRemoteExceptionsAddDialogProps) => {
  const { t } = useTranslation()

  const [postMultipleApprovedRemoteExceptions, isLoading, error, isSuccess, isError] =
    usePostMultipleApprovedRemoteExceptions(queryParams)
  const resolver = useMemo(
    () =>
      yupResolver(
        yup.object({
          persons: yup.array().required(),
          startDate: yup.string().dateOnly().required(),
          endDate: yup.string().dateOnly(),
          reason: yup.string().required(),
        }),
      ),
    [],
  )
  const { control, handleSubmit, setValue, reset } = useForm<FormValues>({
    resolver,
    defaultValues: {
      persons: [],
      startDate: '',
      endDate: '',
      reason: '',
    },
  })
  const onSubmit = (data: FormValues) => {
    const exceptions: ApprovedRemoteExceptionsListDto = {
      personIds: data.persons.map((person) => person.id),
      startDate: formatDate(data.startDate),
      endDate: data.endDate ? formatDate(data.endDate) : undefined,
      reason: data.reason,
    } as ApprovedRemoteExceptionsListDto

    void postMultipleApprovedRemoteExceptions(exceptions).then(() => {
      onCloseAdd()
      reset()
    })
  }

  const onClose = () => {
    onCloseAdd()
    reset()
  }
  const onAdd = (person: Person[]) => {
    setValue('persons', person)
  }

  return (
    <FormsDialog
      onCancel={onClose}
      onConfirm={handleSubmit(onSubmit)}
      title={t('common.title.addApprovedRemoteExceptions')}
      open={open}
      isPending={isLoading}
      isSuccess={isSuccess}
      isError={isError}
    >
      <Stack spacing={1}>
        {error && <Alert severity="error">{error.message}</Alert>}
        <PersonListInput persons={persons} onAdd={onAdd} control={control} name="persons" />
        <Stack direction="row" spacing={1}>
          <ControlledDateOnlyPicker control={control} name="startDate" label={t('person.startDate')} />
          <ControlledDateOnlyPicker control={control} name="endDate" label={t('person.endDate')} />
        </Stack>
        <ControlledTextField control={control} label={t('approvedRemoteExceptions.reason')} name="reason" />
      </Stack>
    </FormsDialog>
  )
}

export default ApprovedRemoteExceptionsAddDialog
